<template>
  <div>
    <!-- 选择位置按钮new -->
            <button
          @click="openMapDialog"
          :style="buttonStyle"
          :aria-label="hasCoordinates ? '选择位置' : '开始定位'"
          :disabled="isInitializing"
          class="location-picker-btn"
    >
      {{ isInitializing ? '⏳ 地图加载中...' : `📍 ${hasCoordinates ? '选择位置' : '开始定位'}` }}
    </button>

    <!-- 使用 teleport 将地图弹窗挂载到 body 上，避免父容器定位限制 -->
    <teleport to="body">
      <!-- 地图弹窗遮罩 -->
      <div 
        v-if="showMapDialog"
        :style="overlayStyle"
        @click="closeMapDialog"
        class="map-overlay"
      >
      <!-- 地图容器 -->
      <div 
        :style="mapContainerStyle"
        @click.stop
        class="map-container"
      >
        <!-- 搜索框 -->
        <div :style="searchContainerStyle">
          <input
            ref="searchInput"
            v-model="searchKeyword"
                         @keyup.enter="searchLocation"
             :style="searchInputStyle"
             :placeholder="isLoading ? '搜索中...' : '搜索地点...'"
             :disabled="isLoading"
             aria-label="搜索地点"
          />
                     <button
             @click="searchLocation"
             :style="searchButtonStyle"
             :disabled="!searchKeyword.trim() || isLoading"
             aria-label="搜索"
           >
             {{ isLoading ? '⏳' : '🔍' }}
           </button>
        </div>

        <!-- 搜索结果列表 -->
        <div 
          v-if="searchResults.length > 0"
          :style="searchResultsStyle"
          class="search-results"
        >
          <div
            v-for="(result, index) in searchResults"
            :key="index"
            @click="selectSearchResult(result)"
            :style="searchResultItemStyle"
            class="search-result-item"
          >
                         <div :style="{ fontWeight: '500', color: '#1f2937' }">{{ result.name }}</div>
             <div :style="{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }">
               {{ result.address }}
               <span v-if="result.district" :style="{ color: '#9ca3af', marginLeft: '8px' }">
                 · {{ result.district }}
               </span>
             </div>
          </div>
        </div>

        <!-- 地图显示区域 -->
        <div 
          ref="mapContainer"
          :style="mapDisplayStyle"
          class="map-display"
        ></div>

        <!-- 中心定位标记 -->
        <div :style="centerMarkerStyle" class="center-marker">
          📍
        </div>

        <!-- 当前地址显示 -->
        <div 
          v-if="currentAddress"
          :style="addressDisplayStyle"
          class="address-display"
        >
          {{ currentAddress }}
        </div>

        <!-- 确认选择按钮 -->
        <button
          @click="confirmSelection"
          :style="confirmButtonStyle"
          :disabled="isLoading"
          class="confirm-btn"
          aria-label="确认选择当前位置"
        >
          {{ isLoading ? '定位中...' : '确认选择' }}
        </button>

        <!-- 关闭按钮 -->
        <button
          @click="closeMapDialog"
          :style="closeButtonStyle"
          class="close-btn"
          aria-label="关闭地图"
        >
          ✕
        </button>
      </div>
    </div>
    </teleport>
  </div>
</template>

<script>
import { ref, reactive, computed, watch, onMounted, onUnmounted, nextTick } from 'vue'

/**
 * 高德地图定位选择组件
 * @component MapLocationPicker
 * @description 提供地图位置选择功能，支持搜索和拖拽定位
 */
export default {
  name: 'MapLocationPicker',
  
  props: {
    /**
     * 初始纬度坐标
     * @type {Number}
     */
    lat: {
      type: Number,
      default: null
    },
    
    /**
     * 初始经度坐标  
     * @type {Number}
     */
    lng: {
      type: Number,
      default: null
    }
  },

  emits: [
    /**
     * 位置选择完成事件
     * @event location-selected
     * @type {Object} location - 选择的位置信息 { lat: Number, lng: Number, address?: String }
     */
    'location-selected'
  ],

  setup(props, { emit }) {
    // 响应式数据
    const showMapDialog = ref(false)
    const mapContainer = ref(null)
    const searchInput = ref(null)
    const searchKeyword = ref('')
    const searchResults = ref([])
    const currentAddress = ref('')
    const isLoading = ref(false)
    const isInitializing = ref(false)

    // 地图相关
    let map = null
    let geocoder = null
    let placeSearch = null
    
    // 当前选中的坐标
    const currentLocation = reactive({
      lat: props.lat !== null ? props.lat : 39.921489,
      lng: props.lng !== null ? props.lng : 116.486409
    })

    // 默认坐标（北京）
    const DEFAULT_LOCATION = [116.486409, 39.921489]

    /**
     * 是否有初始坐标
     */
    const hasCoordinates = computed(() => {
      return props.lat !== null && props.lng !== null
    })

    /**
     * 监听props变化，更新地图中心点
     */
    watch(
      () => [props.lat, props.lng],
      ([newLat, newLng], [oldLat, oldLng]) => {
        // 检查坐标是否真的发生了变化
        if (newLat !== oldLat || newLng !== oldLng) {
          // 更新当前位置
          if (newLat !== null && newLng !== null) {
            console.log('📍 Props坐标变化，更新地图中心:', { lat: newLat, lng: newLng })
            
            // 如果地图已经初始化，则使用平滑过渡更新地图中心点
            if (map) {
              updateMapCenter(newLat, newLng, true)
            } else {
              // 如果地图还未初始化，直接更新位置数据
              currentLocation.lat = newLat
              currentLocation.lng = newLng
            }
          }
        }
      },
      { 
        immediate: false, // 不需要立即执行，因为初始化时已经设置了坐标
        deep: false // 坐标是基本类型，不需要深度监听
      }
    )

    /**
     * 检查高德地图API是否加载
     */
    const checkAmapLoaded = () => {
      return new Promise((resolve, reject) => {
        if (window.AMap) {
          resolve()
          return
        }

        // 动态加载高德地图API
        const script = document.createElement('script')
        script.src = 'https://webapi.amap.com/maps?v=2.0&key=db7f33a6e7e61fa5ba565b5b922c28f4'
        script.onload = () => resolve()
        script.onerror = () => reject(new Error('高德地图API加载失败'))
        document.head.appendChild(script)
      })
    }

    /**
     * 打开地图弹窗
     */
    const openMapDialog = async () => {
      try {
        isInitializing.value = true
        showMapDialog.value = true
        await nextTick()
        await initMap()
      } catch (error) {
        console.error('打开地图失败:', error)
        let errorMessage = '地图加载失败，请检查网络连接'
        
        if (error.message.includes('Geocoder')) {
          errorMessage = '地理编码服务加载失败，请刷新页面重试'
        } else if (error.message.includes('高德地图API加载失败')) {
          errorMessage = '高德地图API加载失败，请检查网络连接'
        }
        
        alert(errorMessage)
        showMapDialog.value = false
      } finally {
        isInitializing.value = false
      }
    }

    /**
     * 关闭地图弹窗
     */
    const closeMapDialog = () => {
      showMapDialog.value = false
      searchResults.value = []
      searchKeyword.value = ''
      currentAddress.value = ''
      
             // 清理地图实例和服务
       if (map) {
         map.destroy()
         map = null
       }
       geocoder = null
       placeSearch = null
    }

    /**
     * 初始化地图
     */
    const initMap = async () => {
      try {
        await checkAmapLoaded()
        
        // 创建地图实例
        map = new window.AMap.Map(mapContainer.value, {
          zoom: 15,
          center: [currentLocation.lng, currentLocation.lat],
          mapStyle: 'amap://styles/normal',
          showLabel: true
        })

        // 加载地理编码和地点搜索插件
        await new Promise((resolve, reject) => {
          window.AMap.plugin(['AMap.Geocoder', 'AMap.PlaceSearch'], function() {
            try {
              // 创建地理编码服务
              geocoder = new window.AMap.Geocoder({
                radius: 1000,
                extensions: 'all'
              })
              
              // 创建地点搜索服务
              placeSearch = new window.AMap.PlaceSearch({
                pageSize: 10,
                pageIndex: 1,
                city: '全国',
                map: map,
                autoFitView: false
              })
              
              resolve()
            } catch (error) {
              reject(error)
            }
          })
        })

        // 监听地图移动事件
        map.on('moveend', handleMapMove)
        
        // 初始获取地址
        await getCurrentAddress()
        
      } catch (error) {
        console.error('地图初始化失败:', error)
        throw error
      }
    }

    /**
     * 处理地图移动事件
     */
    const handleMapMove = async () => {
      const center = map.getCenter()
      currentLocation.lat = center.lat
      currentLocation.lng = center.lng
      await getCurrentAddress()
    }

    /**
     * 获取当前位置的地址信息
     */
    const getCurrentAddress = async () => {
      if (!geocoder) return

      try {
        isLoading.value = true
        const result = await new Promise((resolve, reject) => {
          geocoder.getAddress([currentLocation.lng, currentLocation.lat], (status, data) => {
            if (status === 'complete' && data.regeocode) {
              resolve(data.regeocode.formattedAddress)
            } else {
              reject(new Error('地址解析失败'))
            }
          })
        })
        currentAddress.value = result
      } catch (error) {
        console.error('获取地址失败:', error)
        currentAddress.value = '地址解析失败'
      } finally {
        isLoading.value = false
      }
    }

    /**
     * 搜索地点
     */
    const searchLocation = async () => {
      if (!searchKeyword.value.trim() || !placeSearch) return

      try {
        isLoading.value = true
        searchResults.value = []
        
        // 使用官方 PlaceSearch 插件搜索
        await new Promise((resolve, reject) => {
          placeSearch.search(searchKeyword.value, (status, result) => {
            if (status === 'complete' && result.poiList && result.poiList.pois) {
              searchResults.value = result.poiList.pois.map(poi => ({
                name: poi.name,
                address: poi.address,
                location: [poi.location.lng, poi.location.lat],
                district: poi.adname || poi.cityname || ''
              }))
              resolve()
            } else {
              searchResults.value = []
              console.warn('搜索结果为空或搜索失败:', status, result)
              resolve()
            }
          })
        })
      } catch (error) {
        console.error('搜索失败:', error)
        searchResults.value = []
        alert('搜索失败，请重试')
      } finally {
        isLoading.value = false
      }
    }

    /**
     * 选择搜索结果
     */
    const selectSearchResult = (result) => {
      const [lng, lat] = result.location
      
      // 设置地址信息（搜索结果的地址更精确）
      currentAddress.value = result.address
      
      // 使用平滑过渡更新地图中心点
      if (map) {
        updateMapCenter(lat, lng, true)
      } else {
        // 如果地图未初始化，直接更新位置
        currentLocation.lat = lat
        currentLocation.lng = lng
      }
      
      searchResults.value = []
      searchKeyword.value = ''
    }

    /**
     * 更新地图中心点
     * @param {number} lat - 纬度
     * @param {number} lng - 经度
     * @param {boolean} smooth - 是否平滑过渡
     */
    const updateMapCenter = (lat, lng, smooth = true) => {
      if (!map) return
      
      try {
        if (smooth) {
          // 使用平滑过渡
          map.panTo([lng, lat], 500) // 500ms动画时间
        } else {
          // 直接设置中心点
          map.setCenter([lng, lat])
        }
        
        // 更新当前位置
        currentLocation.lat = lat
        currentLocation.lng = lng
        
        // 获取新位置的地址
        getCurrentAddress()
      } catch (error) {
        console.error('更新地图中心点失败:', error)
      }
    }

    /**
     * 确认选择当前位置
     */
    const confirmSelection = () => {
      const locationData = {
        lat: currentLocation.lat,
        lng: currentLocation.lng
      }
      
      if (currentAddress.value && currentAddress.value !== '地址解析失败') {
        locationData.address = currentAddress.value
      }
      
      emit('location-selected', locationData)
      closeMapDialog()
    }

    // 样式定义
    const buttonStyle = {
      background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
      color: 'white',
      border: 'none',
      borderRadius: '8px',
      padding: '12px 24px',
      fontSize: '14px',
      fontWeight: '500',
      cursor: 'pointer',
      boxShadow: '0 2px 8px rgba(59, 130, 246, 0.3)',
      transition: 'all 0.3s ease',
      outline: 'none'
    }

    const overlayStyle = {
      position: 'fixed',
      top: '0',
      left: '0',
      right: '0',
      bottom: '0',
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: '9999',
      animation: 'fadeIn 0.3s ease'
    }

    const mapContainerStyle = {
      position: 'relative',
      width: window.innerWidth <= 768 ? '95vw' : '80vw',
      height: window.innerWidth <= 768 ? '90vh' : '80vh',
      maxWidth: '1000px',
      maxHeight: '800px',
      backgroundColor: 'white',
      borderRadius: '12px',
      overflow: 'hidden',
      boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
      animation: 'slideIn 0.3s ease'
    }

    const searchContainerStyle = {
      position: 'absolute',
      top: '16px',
      left: '16px',
      right: window.innerWidth <= 768 ? '64px' : '72px', // 移动端和桌面端的适配 
      zIndex: '1000',
      display: 'flex',
      gap: '8px'
    }

    const searchInputStyle = {
      flex: '1',
      padding: '12px 16px',
      border: '1px solid #d1d5db',
      borderRadius: '8px',
      fontSize: '14px',
      outline: 'none',
      backgroundColor: 'white',
      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
    }

    const searchButtonStyle = {
      padding: '12px 16px',
      background: '#3b82f6',
      color: 'white',
      border: 'none',
      borderRadius: '8px',
      cursor: 'pointer',
      fontSize: '14px',
      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
      transition: 'background 0.2s ease'
    }

    const searchResultsStyle = {
      position: 'absolute',
      top: '60px',
      left: '16px',
      right: window.innerWidth <= 768 ? '64px' : '72px', // 与搜索容器保持一致，移动端和桌面端适配
      maxHeight: '200px',
      overflowY: 'auto',
      backgroundColor: 'white',
      borderRadius: '8px',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
      zIndex: '1001',
      border: '1px solid #e5e7eb'
    }

    const searchResultItemStyle = {
      padding: '12px 16px',
      cursor: 'pointer',
      borderBottom: '1px solid #f3f4f6',
      transition: 'background 0.2s ease'
    }

    const mapDisplayStyle = {
      width: '100%',
      height: '100%',
      position: 'relative'
    }

    const centerMarkerStyle = {
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -100%)',
      fontSize: '24px',
      zIndex: '999',
      pointerEvents: 'none',
      filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.3))'
    }

    const addressDisplayStyle = {
      position: 'absolute',
      bottom: '80px',
      left: '16px',
      right: '16px',
      backgroundColor: 'rgba(255, 255, 255, 0.95)',
      padding: '12px 16px',
      borderRadius: '8px',
      fontSize: '14px',
      color: '#374151',
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
      textAlign: 'center',
      zIndex: '1000'
    }

    const confirmButtonStyle = {
      position: 'absolute',
      bottom: '16px',
      left: '50%',
      transform: 'translateX(-50%)',
      background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
      color: 'white',
      border: 'none',
      borderRadius: '8px',
      padding: '12px 32px',
      fontSize: '14px',
      fontWeight: '500',
      cursor: 'pointer',
      boxShadow: '0 4px 12px rgba(59, 130, 246, 0.4)',
      transition: 'all 0.3s ease',
      zIndex: '1000',
      outline: 'none'
    }

    const closeButtonStyle = {
      position: 'absolute',
      top: '16px',
      right: '16px',
      width: '40px',
      height: '40px',
      backgroundColor: 'rgba(255, 255, 255, 0.9)',
      border: 'none',
      borderRadius: '50%',
      cursor: 'pointer',
      fontSize: '16px',
      color: '#6b7280',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
      transition: 'all 0.2s ease',
      zIndex: '1000',
      outline: 'none'
    }

         // 清理函数
     onUnmounted(() => {
       if (map) {
         map.destroy()
       }
       geocoder = null
       placeSearch = null
     })

    return {
      // 数据
      showMapDialog,
      mapContainer,
      searchInput,
      searchKeyword,
      searchResults,
      currentAddress,
      isLoading,
      isInitializing,
      hasCoordinates,
      
      // 方法
      openMapDialog,
      closeMapDialog,
      searchLocation,
      selectSearchResult,
      confirmSelection,
      updateMapCenter,
      
      // 样式
      buttonStyle,
      overlayStyle,
      mapContainerStyle,
      searchContainerStyle,
      searchInputStyle,
      searchButtonStyle,
      searchResultsStyle,
      searchResultItemStyle,
      mapDisplayStyle,
      centerMarkerStyle,
      addressDisplayStyle,
      confirmButtonStyle,
      closeButtonStyle
    }
  }
}
</script>

<style scoped>
/* 动画效果 */
@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes slideIn {
  from { 
    opacity: 0;
    transform: scale(0.9) translateY(-20px);
  }
  to { 
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}

/* 按钮悬停效果 */
.location-picker-btn:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.4) !important;
}

.location-picker-btn:active:not(:disabled) {
  transform: translateY(0);
}

.location-picker-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none !important;
}

/* 搜索结果悬停效果 */
.search-result-item:hover {
  background-color: #f9fafb !important;
}

.search-result-item:last-child {
  border-bottom: none !important;
}

/* 确认按钮悬停效果 */
.confirm-btn:hover {
  transform: translateX(-50%) translateY(-2px) !important;
  box-shadow: 0 6px 16px rgba(59, 130, 246, 0.5) !important;
}

.confirm-btn:disabled {
  opacity: 0.7 !important;
  cursor: not-allowed !important;
}

/* 关闭按钮悬停效果 */
.close-btn:hover {
  background-color: rgba(255, 255, 255, 1) !important;
  color: #374151 !important;
}

/* 响应式处理 */
@media (max-width: 768px) {
  .location-picker-btn {
    padding: 10px 20px !important;
    font-size: 13px !important;
  }
}

/* 滚动条样式 */
.search-results::-webkit-scrollbar {
  width: 6px;
}

.search-results::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.search-results::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}

.search-results::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}
</style>
